/**
 * CustomerAuthContext — the signed-in student, app-wide.
 *
 * Browsing works without signing in, so this is optional context: `status` is
 * 'unauthenticated' for guests. Signing in with Google stores our session token
 * and loads the account. On startup we verify a stored token via /auth/me.
 *
 * It also syncs the account's college into the browsing college (CollegeContext)
 * so a signed-in student lands on their own campus automatically.
 */

/* eslint-disable react-refresh/only-export-components */

import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { authApi } from '@/api/auth.api';
import { CUSTOMER_TOKEN_KEY } from '@/lib/apiClient';
import { useCollege } from '@/context/CollegeContext';

const CustomerAuthContext = createContext(null);

export function CustomerAuthProvider({ children }) {
  const { setCollege, resetCollegeChoice } = useCollege();
  const [customer, setCustomer] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | authenticated | unauthenticated

  const logout = useCallback(() => {
    localStorage.removeItem(CUSTOMER_TOKEN_KEY);
    setCustomer(null);
    setStatus('unauthenticated');
    // Signing out is how a student changes campus, so the choice must not
    // outlive the session — the next sign-in has to ask again.
    resetCollegeChoice();
  }, [resetCollegeChoice]);

  /**
   * Adopt the account's last campus WITHOUT marking it chosen.
   *
   * Used on a page refresh so the catalogue keeps working while the app
   * reloads. It deliberately does not confirm the choice: if the student had
   * not picked yet this session, the picker must still appear.
   */
  const adoptCollege = useCallback(
    (account) => {
      setCollege(account?.college?._id ?? '');
    },
    [setCollege],
  );

  // On startup: verify a stored token, if any.
  useEffect(() => {
    const token = localStorage.getItem(CUSTOMER_TOKEN_KEY);
    if (!token) {
      setStatus('unauthenticated');
      return;
    }
    authApi
      .me()
      .then((account) => {
        setCustomer(account);
        setStatus('authenticated');
        adoptCollege(account);
      })
      .catch(() => {
        localStorage.removeItem(CUSTOMER_TOKEN_KEY);
        setStatus('unauthenticated');
      });
  }, [adoptCollege]);

  // React to a 401 fired by the apiClient mid-session.
  useEffect(() => {
    window.addEventListener('rocket07:customer-unauthorized', logout);
    return () => window.removeEventListener('rocket07:customer-unauthorized', logout);
  }, [logout]);

  /** Sign in with a Google credential. Returns the account. */
  const loginWithGoogle = useCallback(
    async (credential) => {
      const { token, customer: account, isNew } = await authApi.googleLogin(credential);
      localStorage.setItem(CUSTOMER_TOKEN_KEY, token);
      setCustomer(account);
      setStatus('authenticated');
      /**
       * Every sign-in asks for the campus again, returning students included —
       * that is the whole point: signing out and back in is how you correct a
       * campus you picked by mistake. Clearing rather than adopting is what
       * makes CollegePicker appear.
       */
      resetCollegeChoice();
      // isNew travels back so the caller can send a first-time student to fill
      // in their details instead of dropping them on the store.
      return { account, isNew: Boolean(isNew) };
    },
    [resetCollegeChoice],
  );

  /** Update the profile (name/mobile/avatar/college) and refresh state. */
  const updateProfile = useCallback(async (patch) => {
    const account = await authApi.updateProfile(patch);
    setCustomer(account);
    return account;
  }, []);

  const value = {
    customer,
    status,
    isAuthenticated: status === 'authenticated',
    loginWithGoogle,
    logout,
    updateProfile,
  };

  return <CustomerAuthContext.Provider value={value}>{children}</CustomerAuthContext.Provider>;
}

export function useCustomerAuth() {
  const context = useContext(CustomerAuthContext);
  if (!context) throw new Error('useCustomerAuth must be used inside a <CustomerAuthProvider>');
  return context;
}

export default CustomerAuthContext;
